import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { getEscalatorBaseUrl } from '@atlassian/jira-servicedesk-incident-management-common/src/utils';
import type { Action } from '@atlassian/react-sweet-state';
import type { PIRConfigurationActionArgs } from '../../../common/types';
import { SET_PIR_CONFIGURATIONS_ERROR } from '../../constants';
import type { State } from '../../types';
import messages from './messages';

export const setPIRConfiguration =
	({ cloudId, projectId, analyticsEvent }: PIRConfigurationActionArgs): Action<State> =>
	async ({ getState, setState }) => {
		const { pirConfiguration } = getState();
		const { showFlag } = useFlagsService();
		const {
			selectedConfluenceSpaceId,
			selectedConfluenceSpaceName,
			selectedConfluenceParentPageId,
			selectedConfluenceParentPageName,
		} = pirConfiguration;

		if (
			pirConfiguration.isSaving ||
			selectedConfluenceSpaceId === undefined ||
			selectedConfluenceParentPageId === undefined
		) {
			return;
		}

		if (projectId == null) {
			throw new Error('projectId is not set');
		}

		setState({
			pirConfiguration: {
				...pirConfiguration,
				isSaving: true,
			},
		});

		const updateConfiguration = {
			confluenceSpaceId: selectedConfluenceSpaceId,
			confluenceSpaceName: selectedConfluenceSpaceName,
			confluenceParentPageId: selectedConfluenceParentPageId,
			confluenceParentPageName: selectedConfluenceParentPageName,
		};

		try {
			await performPostRequest(
				`${getEscalatorBaseUrl(cloudId)}/project/${projectId}/pir-configuration`,
				{
					body: JSON.stringify(updateConfiguration),
				},
			);

			setState({
				pirConfiguration: {
					...pirConfiguration,
					...updateConfiguration,
					isSaving: false,
				},
			});

			fireOperationalAnalytics(analyticsEvent, 'setPIRConfiguration succeeded');
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			showFlag({
				type: 'error',
				title: messages.pirConfigurationErrorFlagText,
			});

			// we only fire error analytics when it's not a user initiated error. This is because this is a high poll
			// request and we want to avoid being alerted when the requests are cancelled due to SPA transitions
			if (e && e.name !== 'AbortError') {
				fireErrorAnalytics({
					meta: {
						id: 'setPIRConfiguration',
						packageName: 'jiraServicedeskIncidentManagementSettings',
						teamName: 'itsm-agni',
					},
					event: analyticsEvent,
					error: e,
					sendToPrivacyUnsafeSplunk: true,
				});
			}

			setState({
				pirConfiguration: {
					...pirConfiguration,
					isSaving: false,
					error: {
						value: e.message,
						type: SET_PIR_CONFIGURATIONS_ERROR,
					},
				},
			});
		}
	};
